// Attached Club member figures + live staff identification grant for the
// viewed order. Provided once by PosV2ViewWorkspaceBase and injected by the
// member panel, the info pills and the compact money strip so all three
// always agree. Legacy hosts never provide it: consumers must treat a null
// injection as "no club panel".
import { computed, ComputedRef, InjectionKey, Ref, ref, watch } from 'vue';
import { useNow, watchDebounced } from '@vueuse/core';
import { ClubService } from '@/api/services/club.service';
import {
  IClubOrderMember,
  IClubOrderMemberContext,
} from '@/interfaces/IClubMember';
import { IOrder } from '@/interfaces/IOrder';
import {
  verificationDeadline,
  verificationSecondsLeft,
} from '@/domain/club/club-verification';

export interface IOrderClubContext {
  member: Ref<IClubOrderMember | null>;
  /** The cashier's identification grant still covers this order. */
  verified: ComputedRef<boolean>;
  secondsLeft: ComputedRef<number>;
  loading: Ref<boolean>;
  refresh: () => Promise<void>;
  /**
   * Seeds the state from a context the caller already holds (the attach
   * response), so the panel renders at once instead of after a refetch.
   */
  apply: (context: IClubOrderMemberContext) => void;
}

export const ORDER_CLUB_CONTEXT_KEY: InjectionKey<IOrderClubContext> =
  Symbol('orderClubContext');

/**
 * @param enabled False when the viewer may not call the context endpoint
 * (it needs OrdersManage): consumers then fall back to the order's member
 * embed instead of logging a 401 on every order open.
 */
export function useOrderClubContext(
  order: Ref<IOrder>,
  enabled: Ref<boolean> = ref(true)
): IOrderClubContext {
  const member = ref<IClubOrderMember | null>(null);
  const deadlineMs = ref<number | null>(null);
  const shouldFetch = () => enabled.value && !!order.value?.clubMemberId;
  // Starts as loading when there is something to fetch so consumers show a
  // skeleton (not "no balance") during the debounce before the first call.
  const loading = ref(shouldFetch());
  // Out-of-order guard: only the latest request may write state.
  let refreshSeq = 0;

  const now = useNow({ interval: 1000 });
  const secondsLeft = computed(() =>
    deadlineMs.value == null
      ? 0
      : verificationSecondsLeft(deadlineMs.value, now.value.getTime())
  );
  const verified = computed(() => secondsLeft.value > 0);

  async function refresh(): Promise<void> {
    const seq = ++refreshSeq;
    const current = order.value;

    if (!shouldFetch()) {
      member.value = null;
      deadlineMs.value = null;
      loading.value = false;
      return;
    }

    loading.value = true;
    try {
      const context = await ClubService.getOrderMemberContext(current.id);
      if (seq !== refreshSeq) return;
      setContext(context);
    } catch (e) {
      // No toast: the panel falls back to the order's member embed.
      if (seq === refreshSeq) console.error('Error loading club context', e);
    } finally {
      if (seq === refreshSeq) loading.value = false;
    }
  }

  function setContext(context: IClubOrderMemberContext): void {
    member.value = context.member;
    deadlineMs.value = context.verification
      ? verificationDeadline(context.verification.ttlSeconds, Date.now())
      : null;
  }

  function apply(context: IClubOrderMemberContext): void {
    // Supersedes any request in flight: its stale answer must not overwrite
    // the fresher state seeded here.
    refreshSeq++;
    setContext(context);
    loading.value = false;
  }

  // Another order / member: drop the previous figures at once so the
  // debounced refetch below never shows them under the new order's name.
  watch(
    [() => order.value?.id, () => order.value?.clubMemberId],
    () => {
      refreshSeq++;
      member.value = null;
      deadlineMs.value = null;
      loading.value = shouldFetch();
    }
  );

  // Every club action ends in a refreshed order (REST refresh or WS
  // v2:orderUpdate), which bumps updatedAt — no extra wiring needed.
  const refreshKey = computed(
    () =>
      `${enabled.value}|${order.value?.id ?? ''}|${
        order.value?.clubMemberId ?? ''
      }|${String(order.value?.updatedAt ?? '')}`
  );
  watchDebounced(
    refreshKey,
    () => {
      void refresh();
    },
    { debounce: 300 }
  );
  void refresh();

  return { member, verified, secondsLeft, loading, refresh, apply };
}
